import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface IndividualExportsSectionProps {
  isExporting: boolean;
  onExportObjects: () => void;
  onExportTemplates: () => void;
  onExportTexts: () => void;
  onExportDocuments: () => void;
}

export function IndividualExportsSection({
  isExporting,
  onExportObjects,
  onExportTemplates,
  onExportTexts,
  onExportDocuments
}: IndividualExportsSectionProps) {
  const { t } = usePlatformTranslation();

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <h4 className="heading-4">Individual Exports</h4>
      <p className="text-muted-foreground mb-6">
        Export specific data types separately if you only need certain components
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Objects Export */}
        <div className="border border-border rounded p-4 hover:border-primary/50 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <IconProvider iconstring="fa-cube" size={16} className="text-primary" />
            <h4 className="heading-4">{t('platform.settings.importexportsection.objects')}</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Export all glossary items with their fields and data
          </p>
          <Button
            onClick={onExportObjects}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <IconProvider iconstring="fa-download" className="mr-2" size={16} />
            Export Objects
          </Button>
        </div>

        {/* Templates Export */}
        <div className="border border-border rounded p-4 hover:border-primary/50 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <IconProvider iconstring="fa-layer-group" size={16} className="text-primary" />
            <h4 className="heading-4">{t('platform.settings.importexportsection.templates')}</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Export all blueprints and template definitions
          </p>
          <Button
            onClick={onExportTemplates}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <IconProvider iconstring="fa-download" className="mr-2" size={16} />
            Export Templates
          </Button>
        </div>

        {/* Texts Export */}
        <div className="border border-border rounded p-4 hover:border-primary/50 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <IconProvider iconstring="fa-language" size={16} className="text-primary" />
            <h4 className="heading-4">{t('platform.settings.importexportsection.texts')}</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Export all text strings with translations
          </p>
          <Button
            onClick={onExportTexts}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <IconProvider iconstring="fa-download" className="mr-2" size={16} />
            Export Texts
          </Button>
        </div>

        {/* Documents Export */}
        <div className="border border-border rounded p-4 hover:border-primary/50 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <IconProvider iconstring="fa-file-alt" size={16} className="text-primary" />
            <h4 className="heading-4">{t('platform.settings.importexportsection.documents')}</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Export all documents with their content blocks
          </p>
          <Button
            onClick={onExportDocuments}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <IconProvider iconstring="fa-download" className="mr-2" size={16} />
            Export Documents
          </Button>
        </div>
      </div>
    </div>
  );
}
